import { Field, Text, Placeholder, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Icon from 'components/helpers/Icon';
import TextInput from 'components/helpers/TextInput/TextInput';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import Select, { type SelectOption } from 'components/helpers/Select/Select';
import classNames from 'classnames/bind';
import React, { useEffect } from 'react';
import { getCounties } from '../API';
import { County } from 'models/plans-search-types';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import styles from './PlanSearchSar.module.scss';
import { useI18n } from 'next-localization';
import { PlanSearchSarProps, SarPlans, SarSetupFields, PlanTypeFields } from '../Types/PlanSearchTypes.Type';
import { getLanguageBasedUrl } from 'lib/utils';

const cx = classNames.bind(styles);

const PlanSearchSar = (props: PlanSearchSarProps): React.JSX.Element => {
  const { fields, rendering } = props;
  const { t } = useI18n();
  const {
    heading,
    fieldsRequiredLabel,
    disclaimerText,
    countyLabel,
    zipcodeLabel,
    zipcodeErrorMessage,
    zipcodeInvalidErrorMessage,
    countyErrorMessage,
    sarPlans,
    sarSetup,
    submitLabel,
  } = fields;
  const [enteredZipcode, setZipcode] = React.useState<string>('');
  const [selectedCounty, setSelectedCounty] = React.useState<County>({
    CountyName: '',
    StateAbbrev: '',
    PlansByYear: [],
  });
  const [countyOptions, setCountyOptions] = React.useState<SelectOption[]>([]);
  const [counties, setCounties] = React.useState<County[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<Field<string>>();
  const [matchingPlans, setMatchingPlans] = React.useState<SarPlans[]>([]);
  const [showError, setShowError] = React.useState<boolean>(false);
  const [chosenPlan, setChosenPlan] = React.useState<SarPlans | null>(null);
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;

  const sarSetupFields = sarSetup.fields as SarSetupFields;
  const typeFields = sarSetupFields.planType.fields as PlanTypeFields;

  const headingTokenReplaced = heading?.value?.replaceAll(
    '{type}',
    typeFields != undefined ? typeFields.name?.value : ''
  );

  const checkZip: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    setMatchingPlans([]);
    const zip = e.target.value;
    setZipcode(zip);
    setChosenPlan(null);
    //Zipcode is a 5 digit number
    if (zip.length == 5) {
      setShowError(false);
      const tempCounties = await getCounties(zip, sarSetupFields.planType.id ?? '');
      if (tempCounties.length == 0) {
        setCounties([]);
        setErrorMessage(countyErrorMessage);
        setShowError(true);
      } else if (tempCounties.length == 1) {
        setCounties(tempCounties);
        setSelectedCounty(tempCounties[0]);
        checkPlans(tempCounties[0]);
      } else {
        setCounties(tempCounties);
        const options: SelectOption[] = [];

        for (let x = 0; x < tempCounties.length; x++) {
          const option: SelectOption = {
            label: { value: tempCounties[x].CountyName },
            value: x.toString(),
          };
          options.push(option);
        }
        setCountyOptions(options);
      }
    } else {
      setShowError(false);
      setErrorMessage(zipcodeInvalidErrorMessage);
      setCountyOptions([]);
    }
  };

  const changeCounty: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const num = Number.parseInt(e.target.value) ?? -1;
    setErrorMessage(zipcodeErrorMessage);
    setShowError(num < 0);
    setSelectedCounty(counties[num]);
    checkPlans(counties[num]);
  };

  const checkPlans = (county: County) => {
    let found = false;
    setMatchingPlans([]);
    sarPlans.forEach((plan) => {
      const countyList = plan.counties.split('|') ?? [];
      countyList.forEach((planCounty) => {
        if (planCounty == county.CountyName) {
          setMatchingPlans((prev) => [...prev, plan]);
          found = true;
        }
      });
    });
    if (!found) {
      setErrorMessage(countyErrorMessage);
      setShowError(true);
    }
    setChosenPlan(null);
  };

  useEffect(() => {
    if (matchingPlans.length === 1) {
      setChosenPlan(matchingPlans[0]);
    }
  }, [matchingPlans]);

  //&state=PA&county=Allegheny&year=2025&zipcode=15017&ExistingMember=false
  const selectedPlan: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const plan = matchingPlans.find((p) => p.id === e.target.value);
    setChosenPlan(plan ?? null);
  };

  const SearchAndContinue: React.MouseEventHandler = () => {
    if (!chosenPlan) return;

    const suggestions = chosenPlan.suggestedPlans.split('|') ?? [];
    const baseURL = getLanguageBasedUrl(sarSetupFields.comparisonPage?.url || '', sitecoreContext.language);
    const params = new URLSearchParams({
      plan0: chosenPlan.id,
      plan1: suggestions[0] || '',
      state: selectedCounty.StateAbbrev,
      county: selectedCounty.CountyName,
      year: sarSetupFields.upgradeYear.name,
      zipcode: enteredZipcode,
      ExistingMember: 'true',
    });

    if (suggestions.length === 2) {
      params.append('plan2', suggestions[1]);
    }

    window.location.href = `${baseURL}?${params.toString()}`;
  };

  return (
    <section className={cx('plan-search-wrapper')}>
      <div className={cx('plan-search')}>
        <div>
          {pageEditing ? (
            <Text tag="h1" className="font-style-xl" encode={sitecoreContext.pageEditing} field={heading} />
          ) : (
            <h1 className="font-style-xl" dangerouslySetInnerHTML={{ __html: headingTokenReplaced }} />
          )}
          <Text tag="p" encode={sitecoreContext.pageEditing} field={fieldsRequiredLabel} />
        </div>
        <div className={cx('entryBoxes')}>
          <div className={cx('entryHalf')}>
            <Text field={zipcodeLabel} htmlFor="zipcode" encode={pageEditing} tag="label" />
            <br />
            <TextInput
              value={enteredZipcode}
              maxLength={5}
              type="number"
              id="zipcode"
              pattern="^[0-9]{5}$"
              placeholder={t('Zip Code')}
              onChange={checkZip}
              className={cx('inputBox')}
            />
            {showError && (
              <div role="alert" className={cx('error-msg')}>
                <Icon variant="error" />
                <Text htmlFor="zipcode" field={errorMessage} encode={pageEditing} />
              </div>
            )}
          </div>

          {countyOptions.length > 1 && (
            <div className={cx('entryHalf')}>
              <Select
                id="county"
                options={countyOptions}
                onChange={changeCounty}
                className={cx('inputBox')}
                label={countyLabel.value}
              />
            </div>
          )}
        </div>
        {matchingPlans.length > 1 && (
          <div className={cx('entryBox')}>
            <Select
              id="plan"
              options={matchingPlans.map((plan) => ({
                label: { value: plan.name },
                value: plan.id,
              }))}
              onChange={selectedPlan}
              className={cx('inputBox')}
              label={t('Select a Plan')}
            />
          </div>
        )}
        <p>
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            className={cx('loginButton')}
            onClick={SearchAndContinue}
            disabled={!chosenPlan}
          >
            <Text field={submitLabel} />
          </Button>
        </p>
        <RichTextWrapper className={cx('plan-search__disclaimer')} field={disclaimerText} />
      </div>
      <aside className={cx('plan-search-listing__aside')}>
        <Placeholder key={0} name="hp-container-3070-right" rendering={rendering} disableSuspense={true} />
      </aside>
    </section>
  );
};

export default compose<PlanSearchSarProps>(withValidDatasourceCheck(), withStyles())(PlanSearchSar);
